import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Clock, Check } from 'lucide-react';

const TimeRangeSlider = ({ onTimeChange, onClose }) => {
  const MIN_TIME = 8 * 60;  // 8:00 AM
  const MAX_TIME = 22 * 60; // 10:00 PM
  const sliderRef = useRef(null);

  const getCurrentTime = useCallback(() => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  }, []);

  const [range, setRange] = useState(() => {
    const currentTime = getCurrentTime();
    const startTime = Math.max(MIN_TIME, Math.min(currentTime, MAX_TIME));
    const endTime = Math.min(startTime + 60, MAX_TIME);
    return { start: startTime, end: endTime };
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sliderRef.current && !sliderRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const formatTimeDisplay = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 === 0 ? 12 : hours % 12;
    return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const getPercentage = (minutes) => {
    return ((minutes - MIN_TIME) / (MAX_TIME - MIN_TIME)) * 100;
  };

  const snapToGrid = (minutes) => {
    const gridSize = 15; 
    return Math.round(minutes / gridSize) * gridSize;
  };

  const handlePresetClick = (hours) => {
    const currentTime = getCurrentTime();
    const start = Math.max(MIN_TIME, Math.min(currentTime, MAX_TIME));
    const end = Math.min(start + (hours * 60), MAX_TIME);
    
    const newRange = { start, end };
    setRange(newRange);
    onTimeChange({ ...newRange, duration: end - start });
    onClose();
  };

  const handleInteraction = (handle, clientX) => {
    const slider = sliderRef.current.querySelector('.slider-track');
    const rect = slider.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    const rawMinutes = MIN_TIME + (percentage / 100) * (MAX_TIME - MIN_TIME);
    const snappedMinutes = snapToGrid(rawMinutes);

    setRange(prev => {
      if (handle === 'start') {
        return { ...prev, start: Math.min(snappedMinutes, prev.end - 30) };
      } else {
        return { ...prev, end: Math.max(snappedMinutes, prev.start + 30) };
      }
    });
  };

  const startInteraction = (handle) => (e) => {
    e.preventDefault();
    const handleMove = (moveEvent) => {
      const clientX = moveEvent.type.includes('mouse') 
        ? moveEvent.clientX 
        : moveEvent.touches[0].clientX;
      handleInteraction(handle, clientX);
    };
    const handleUp = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleUp);
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.addEventListener('touchmove', handleMove);
    document.addEventListener('touchend', handleUp);
  };

  return (
    <div 
      ref={sliderRef}
      className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[90vw] max-w-[360px] 
                 bg-[#111] border border-white/10 rounded-2xl shadow-2xl shadow-black/50 p-6 z-50"
    >
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2 text-white font-medium">
          <Clock className="w-4 h-4 text-purple-400" />
          <span>Select Time</span>
        </div>
        <span className="text-xs font-medium px-2 py-1 rounded bg-white/5 text-gray-400 border border-white/5">
          {formatDuration(range.end - range.start)}
        </span>
      </div>

      {/* Presets */}
      <div className="grid grid-cols-3 gap-2 mb-8">
        {[1, 2, 3].map((hours) => (
          <button
            key={hours}
            onClick={() => handlePresetClick(hours)}
            className="px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                     bg-white/5 hover:bg-white/10 text-gray-300 hover:text-white border border-white/5"
          >
            +{hours} hour{hours > 1 ? 's' : ''}
          </button>
        ))}
      </div>

      {/* Time Display */}
      <div className="flex justify-between items-end mb-6 px-2">
        <div className="text-center">
          <div className="text-xs text-gray-500 mb-1 uppercase tracking-wider font-bold">Start</div>
          <div className="text-2xl font-bold text-white tabular-nums tracking-tight">
            {formatTimeDisplay(range.start)}
          </div>
        </div>
        <div className="h-px flex-1 bg-white/10 mx-4 mb-4" />
        <div className="text-center">
          <div className="text-xs text-gray-500 mb-1 uppercase tracking-wider font-bold">End</div>
          <div className="text-2xl font-bold text-white tabular-nums tracking-tight">
            {formatTimeDisplay(range.end)}
          </div>
        </div>
      </div>

      {/* Slider */}
      <div className="relative h-8 mb-8 group slider-track cursor-pointer">
        {/* Track */}
        <div className="absolute top-1/2 -translate-y-1/2 w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
          {/* Active Region */}
          <div 
            className="absolute h-full bg-purple-500"
            style={{
              left: `${getPercentage(range.start)}%`,
              right: `${100 - getPercentage(range.end)}%`
            }}
          />
        </div>

        {/* Thumbs */}
        {[
          { value: range.start, handle: 'start' },
          { value: range.end, handle: 'end' }
        ].map(({ value, handle }) => (
          <div
            key={handle}
            className="absolute top-1/2 -translate-y-1/2 w-6 h-6 bg-white rounded-full shadow-lg cursor-grab active:cursor-grabbing
                     flex items-center justify-center hover:scale-110 transition-transform z-10"
            style={{ left: `${getPercentage(value)}%`, transform: 'translate(-50%, -50%)' }}
            onMouseDown={startInteraction(handle)}
            onTouchStart={startInteraction(handle)}
          >
            <div className="w-1.5 h-1.5 bg-purple-500 rounded-full" />
          </div>
        ))}
      </div>

      {/* Apply Button */}
      <button
        onClick={() => {
          onTimeChange({ ...range, duration: range.end - range.start });
          onClose();
        }}
        className="w-full py-3 rounded-xl bg-white text-black font-bold text-sm
                 hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
      >
        <Check className="w-4 h-4" />
        Apply Filter
      </button>
    </div>
  );
};

export default TimeRangeSlider;